import { graphql } from 'react-relay';
import type { Disposable, IEnvironment } from 'relay-runtime';
import { requestSubscription } from 'relay-runtime';
import type { ADFEntity } from '@atlaskit/adf-utils/types';

import {
	type ADFFetchProvider,
	type BlockSubscriptionErrorCallback,
	type BlockUpdateCallback,
	generateBlockAriFromReference,
	type ResourceId,
	type SyncBlockInstance,
	type SyncBlockProduct,
	SyncBlockStatus,
	type Unsubscribe,
} from '@atlaskit/editor-synced-block-provider';

import type { relaySyncBlockFetchProviderSubscription } from './__generated__/relaySyncBlockFetchProviderSubscription.graphql.ts';

/**
 * Gets the Relay environment from the global window object.
 * Confluence stores the singleton environment in window['GLOBAL_RELAY_ENV'].
 */
function getGlobalRelayEnvironment(): IEnvironment | null {
	if (typeof window !== 'undefined') {
		const globalWindow = window as unknown as { GLOBAL_RELAY_ENV?: IEnvironment };
		if (globalWindow.GLOBAL_RELAY_ENV) {
			return globalWindow.GLOBAL_RELAY_ENV;
		}
	}
	return null;
}

/**
 * Converts a Relay subscription response to SyncBlockInstance format.
 */
function convertRelayResponseToSyncBlockInstance(
	response: NonNullable<
		relaySyncBlockFetchProviderSubscription['response']['blockService_onBlockUpdated']
	>,
	resourceId: ResourceId,
): SyncBlockInstance {
	let createdAt: string | undefined;
	if (response.createdAt !== undefined && response.createdAt !== null) {
		try {
			// BE returns microseconds, convert to milliseconds
			createdAt = new Date(response.createdAt / 1000).toISOString();
		} catch {
			createdAt = undefined;
		}
	}

	return {
		data: {
			content: JSON.parse(response.content) as ADFEntity[],
			resourceId: response.blockAri,
			blockInstanceId: response.blockInstanceId,
			sourceAri: response.sourceAri,
			product: response.product as SyncBlockProduct,
			createdAt,
			createdBy: response.createdBy,
			status: response.status as SyncBlockStatus,
		},
		resourceId,
	};
}

/**
 * Creates a Relay-based ADFFetchProvider wrapper that adds Relay subscription support
 * to an existing fetch provider.
 *
 * @param baseProvider - The base ADFFetchProvider to wrap (e.g., BlockServiceADFFetchProvider)
 * @param relayEnvironment - Optional Relay environment. If not provided, will attempt to use window.GLOBAL_RELAY_ENV
 * @param cloudId - The cloud ID for constructing block ARIs
 * @returns A new ADFFetchProvider with Relay subscription support
 */
export function createRelaySyncBlockFetchProvider(
	baseProvider: ADFFetchProvider,
	cloudId: string,
	relayEnvironment?: IEnvironment,
): ADFFetchProvider {
	const subscription = graphql`
		subscription relaySyncBlockFetchProviderSubscription($resourceId: ID!) {
			blockService_onBlockUpdated(resourceId: $resourceId) {
				blockAri
				blockInstanceId
				content
				contentUpdatedAt
				createdAt
				createdBy
				deletionReason
				product
				sourceAri
				status
			}
		}
	`;

	// Create a new provider that preserves all methods from baseProvider
	// Preserve the prototype chain to ensure all methods are accessible
	const wrappedProvider = Object.create(Object.getPrototypeOf(baseProvider));
	Object.assign(wrappedProvider, baseProvider);
	// Override subscribeToBlockUpdates with Relay subscription support
	wrappedProvider.subscribeToBlockUpdates = function (
		resourceId: ResourceId,
		onUpdate: BlockUpdateCallback,
		onError?: BlockSubscriptionErrorCallback,
	): Unsubscribe {
		// Get Relay environment from parameter or global window
		const environment = relayEnvironment ?? getGlobalRelayEnvironment();
		if (!environment) {
			onError?.(new Error('Relay environment not available'));
			return () => {};
		}

		// Convert resourceId to blockAri for the subscription
		const blockAri = generateBlockAriFromReference({ cloudId, resourceId });

		let disposable: Disposable | null = null;

		try {
			disposable = requestSubscription<relaySyncBlockFetchProviderSubscription>(environment, {
				subscription,
				variables: { resourceId: blockAri },
				onNext: (response) => {
					if (response?.blockService_onBlockUpdated) {
						const syncBlockInstance = convertRelayResponseToSyncBlockInstance(
							response.blockService_onBlockUpdated,
							resourceId,
						);
						onUpdate(syncBlockInstance);
					}
				},
				onError: (error) => {
					onError?.(error);
				},
			});
		} catch (error) {
			onError?.(error instanceof Error ? error : new Error(String(error)));
			// Return a no-op unsubscribe if subscription fails
			return () => {};
		}

		return () => {
			if (disposable) {
				disposable.dispose();
			}
		};
	};

	return wrappedProvider;
}
